// ============================================================================
// Moah AI Trader — Frontend API Client
//
// REAL DATA ONLY. There is no mock data layer: every call goes to the live
// Cloudflare Worker API. If the API URL isn't configured or the user isn't
// authenticated, calls return { success: false } and pages render empty states.
// ============================================================================

type ApiResult<T = any> = { success: boolean; data?: T; summary?: any; error?: string };

const RAW_API_URL = (process.env.NEXT_PUBLIC_API_URL || '').trim().replace(/\/+$/, '');
// Normalize: strip any trailing /api/v1 so we never double-prefix the base URL
const REAL_API_URL =
  RAW_API_URL && !RAW_API_URL.includes('YOUR-SUBDOMAIN') ? RAW_API_URL.replace(/\/api\/v1$/, '') : '';

const NOT_CONFIGURED: ApiResult<never> = { success: false, error: 'API is not configured' };
const NOT_AUTHENTICATED: ApiResult<never> = { success: false, error: 'Not authenticated' };

function isToken(arg: any): arg is string {
  return typeof arg === 'string' && arg.length > 20;
}

function hasToken(args: any[]): string | undefined {
  for (let i = args.length - 1; i >= 0; i--) {
    if (isToken(args[i])) return args[i];
  }
  return undefined;
}

async function realFetch<T = any>(path: string, token: string, init?: RequestInit): Promise<ApiResult<T>> {
  try {
    const res = await fetch(`${REAL_API_URL}/api/v1${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
        ...(init?.headers || {}),
      },
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok || body.success === false) {
      return { success: false, error: body.error || `API error (${res.status})` };
    }
    return { success: true, data: body.data, summary: body.summary ?? body.data?.summary };
  } catch (err: any) {
    return { success: false, error: err?.message || 'Network error' };
  }
}

/**
 * Resolve the auth token for a request. Returns the token, or a failure
 * result if the API isn't configured or nobody is logged in.
 */
function requireToken(args: any[]): { token: string } | { fail: ApiResult<never> } {
  if (!REAL_API_URL) return { fail: NOT_CONFIGURED };
  const token = hasToken(args);
  if (!token) return { fail: NOT_AUTHENTICATED };
  return { token };
}

export const api = {
  async getSettings(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/settings', r.token);
  },

  async updateSettings(settings: any, ...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/settings', r.token, { method: 'PATCH', body: JSON.stringify(settings) });
  },

  async startBot(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/bot/start', r.token, { method: 'POST' });
  },

  async stopBot(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/bot/stop', r.token, { method: 'POST' });
  },

  async emergencyStop(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/bot/emergency-stop', r.token, { method: 'POST' });
  },

  async enableLive(_confirmation: any, ...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/bot/enable-live', r.token, {
      method: 'POST',
      body: JSON.stringify({ confirm: true }),
    });
  },

  async getSignals(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    const limit = typeof args[0] === 'number' ? args[0] : 20;
    return realFetch(`/signals?limit=${limit}`, r.token);
  },

  async getStats(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/performance/daily', r.token);
  },

  async getDailyStats(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/performance/daily', r.token);
  },

  async getOpenPositions(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/positions', r.token);
  },

  async closePosition(positionId: string, ...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch(`/positions/${positionId}/close`, r.token, { method: 'POST' });
  },

  async getPerformance(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/performance', r.token);
  },

  async runBacktest(config: any, ...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/backtest/run', r.token, { method: 'POST', body: JSON.stringify(config) });
  },

  async getTrades(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    const params = args[0] && typeof args[0] === 'object' ? args[0] : undefined;
    const q = new URLSearchParams();
    if (params?.limit) q.set('limit', String(params.limit));
    if (params?.offset) q.set('offset', String(params.offset));
    if (params?.filter && params.filter !== 'all') q.set('filter', params.filter);
    if (params?.mode) q.set('mode', params.mode);
    if (params?.result) q.set('result', params.result);
    return realFetch(`/trades?${q.toString()}`, r.token);
  },

  async getPaperTrades(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/paper/trades', r.token);
  },

  async getBotLogs(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    const q = new URLSearchParams();
    if (typeof args[0] === 'number') q.set('limit', String(args[0]));
    else if (args[0] && typeof args[0] === 'object') {
      if (args[0].level && args[0].level !== 'all') q.set('level', args[0].level);
      if (args[0].search) q.set('search', args[0].search);
      if (args[0].limit) q.set('limit', String(args[0].limit));
    }
    return realFetch(`/logs/bot?${q.toString()}`, r.token);
  },

  async getAuditLogs(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    const limit = typeof args[0] === 'number' ? args[0] : (args[0]?.limit ?? 50);
    return realFetch(`/logs/audit?limit=${limit}`, r.token);
  },

  async getAdminStats(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/admin/stats', r.token);
  },

  async connectDeriv(apiToken: string, ...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/account/connect', r.token, {
      method: 'POST',
      body: JSON.stringify({ api_token: apiToken }),
    });
  },

  async getDerivAccount(...args: any[]): Promise<ApiResult> {
    const r = requireToken(args);
    if ('fail' in r) return r.fail;
    return realFetch('/account/info', r.token);
  },
};
